import { useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { FormattedMessage } from 'react-intl';
import {
  GeneratedReport,
  ReportDescriptionResponse,
  ReportType,
} from '../../../reports/models/report';

import { ApplicationSuccessResult } from '../../../models/ApplicationSuccessResult';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { messages as bundledFlowMessages } from '../../../reports/components/BundledFlow/molecules/messages';
import { AccountList } from '../../../reports/components/BundledFlow/molecules/AccountList';
import { SuccessNotification } from '../../../core/screens/SuccessNotification/SuccessNotification';
import { createBusinessAccountCheckMachine } from '../businessAccountCheckMachine';
import { ReduxCredentials } from '../../../components/screens/SubmitCredentials/ReduxCredentials';
import { ProviderEntity } from '../../../models/provider';
import { useIdempotencyKey } from '../../../reports/useIdempotencyKey';

export const makeApplicationResultFromReports = (reports: GeneratedReport[]) => {
  const businessAccountCheckReport = reports.find(
    (report) => report.reportType === ReportType.BUSINESS_ACCOUNT_VERIFICATION_REPORT
  );

  return ApplicationSuccessResult.businessAccountCheck({
    businessAccountCheckReportId: businessAccountCheckReport!.reportId,
  });
};

const reportTypes = [ReportType.BUSINESS_ACCOUNT_VERIFICATION_REPORT];
export type BusinessAccountCheckMachineViewProps = {
  customerReferenceId?: string;
  externalReference?: string;
  selectedProvider: ProviderEntity;
  reportDescription: ReportDescriptionResponse;
  onCancel: () => void;
};

// istanbul ignore next: this component is a switch.
export const BusinessAccountCheckMachineView = ({
  customerReferenceId,
  externalReference,
  selectedProvider,
  onCancel,
  reportDescription,
}: BusinessAccountCheckMachineViewProps) => {
  const { refreshItems, accountDialogType } = reportDescription;

  const idempotencyKey = useIdempotencyKey();

  const machine = useMemo(
    () =>
      createBusinessAccountCheckMachine({
        accountDialogType,
        getAccountsPayload: {
          reportTypes,
          refreshableItems: refreshItems,
          accountDialogType,
        },
        generateReportsPayload: {
          reportTypes,
          customerReferenceId,
          externalReference,
          refreshableItems: refreshItems,
          accountDialogType,
        },
      }),
    [accountDialogType, customerReferenceId, externalReference, refreshItems]
  );
  const [state, send] = useMachine(machine);

  const { accountsResponse, createReportResponse, error } = state.context;

  if (state.matches('auth')) {
    return (
      <ReduxCredentials
        onBack={undefined}
        onDone={() => {
          send('SET_IDEMPOTENCY_KEY', { data: idempotencyKey });
          send('CREDENTIALS');
        }}
        provider={selectedProvider}
        refreshableItems={reportDescription.refreshItems}
      />
    );
  }

  if (state.matches('SUCCESS')) {
    const applicationSuccessResult = makeApplicationResultFromReports(createReportResponse!);

    return (
      <SuccessNotification
        applicationSuccessResult={applicationSuccessResult}
        forceSkipSuccessScreen
      />
    );
  }

  if (state.matches('ERROR')) {
    throw error;
  }

  if (state.matches('SINGLE_ACCOUNT_SELECTION_VIEW')) {
    return (
      <AccountList
        title={<FormattedMessage {...bundledFlowMessages.selectAccount} />}
        accounts={accountsResponse!}
        allowSelectMultipleAccounts={false}
        providerDisplayName={selectedProvider.displayName}
        onCancel={onCancel}
        onSubmit={(accountIds) => {
          send('EVENT_ACCOUNTS_SELECTED', {
            data: accountIds,
          });
        }}
      />
    );
  }

  if (state.matches('MULTIPLE_ACCOUNT_SELECTION_VIEW')) {
    return (
      <AccountList
        title={<FormattedMessage {...bundledFlowMessages.selectAccounts} />}
        accounts={accountsResponse!}
        allowSelectMultipleAccounts
        providerDisplayName={selectedProvider.displayName}
        onCancel={onCancel}
        onSubmit={(accountIds) => {
          send('EVENT_ACCOUNTS_SELECTED', {
            data: accountIds,
          });
        }}
      />
    );
  }

  return <LoadingOverlay />;
};
